import { Link } from "@tanstack/react-router";
import { Home } from "lucide-react";

import { MarketingLogo } from "./logo";
import { MarketingStyles } from "./marketing-styles";
import { SiteFooter } from "./site-footer";

const quickLinks = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Integrations", to: "/integrations" },
  { label: "Blog", to: "/blog" },
] as const;

export function NotFoundPage() {
  return (
    <div className="pd-marketing flex min-h-screen flex-col overflow-x-hidden font-sans text-foreground antialiased">
      <MarketingStyles />
      {/*
       * A bare lockup rather than the full SiteHeader. The 404 is reachable from
       * every route, so it is part of the root bundle — and SiteHeader pulls in
       * the mega-menus and the auth client behind them, which is a lot of
       * JavaScript for every visitor to carry against the chance of a bad URL.
       * The page already offers its own way onward, below.
       */}
      <header className="border-border/70 border-b bg-background/70">
        <div className="mx-auto flex h-[68px] max-w-[1200px] items-center px-6">
          <MarketingLogo />
        </div>
      </header>
      <main>
        <section className="relative flex flex-1 items-center justify-center overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 blur-[28px]"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%,oklch(0.72 0.16 268 / 0.22),transparent 70%)",
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(oklch(0.5 0.05 264 / 0.05) 1px,transparent 1px),linear-gradient(90deg,oklch(0.5 0.05 264 / 0.05) 1px,transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage:
                "radial-gradient(70% 60% at 50% 50%,#000 0%,transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(70% 60% at 50% 50%,#000 0%,transparent 75%)",
            }}
          />
          <div className="relative max-w-[600px] px-6 py-[70px] text-center">
            <div
              className="bg-clip-text font-heading text-[clamp(90px,16vw,168px)] text-transparent leading-none tracking-[-0.03em]"
              style={{
                backgroundImage:
                  "linear-gradient(135deg,oklch(0.56 0.17 264),oklch(0.56 0.18 288))",
              }}
            >
              404
            </div>
            <h1 className="mt-2 font-heading font-normal text-[clamp(28px,4vw,40px)] text-foreground tracking-[-0.015em]">
              This page floated off into the feed.
            </h1>
            <p className="mx-auto mt-3.5 max-w-[440px] text-[17px] text-[oklch(0.46_0.02_264)] leading-relaxed">
              The link may be broken or the page may have moved. Let's get you
              back to something useful.
            </p>
            <div className="mt-[30px] flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="pd-brand-grad inline-flex h-[50px] items-center gap-2 rounded-[13px] px-6 font-semibold text-[15.5px] text-white no-underline shadow-[0_12px_30px_-10px_oklch(0.55_0.16_268/0.6)]"
              >
                <Home className="size-4" strokeWidth={2.2} />
                Back home
              </Link>
              <Link
                to="/help"
                className="inline-flex h-[50px] items-center rounded-[13px] border border-border bg-card px-[22px] font-semibold text-[15.5px] text-[oklch(0.28_0.02_264)] no-underline"
              >
                Visit help center
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap justify-center gap-2">
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="inline-flex h-8 items-center rounded-full border border-border bg-card px-3.5 font-medium text-[13.5px] text-[oklch(0.42_0.02_264)] no-underline transition-colors hover:border-[oklch(0.82_0.03_264)] hover:text-primary"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
